import type { BatchProcessesReadmodel } from '@bett3r-dev/teselly-domain';
import { isIntegrationCredentialRevoked } from '@domains/integrations/shared/state/revokedIntegrations';

type Coordinate = { connectorId: string; integrationUserId: string };

const isCoordinate = ( value: unknown ): value is Coordinate =>
  typeof value === 'object' && value !== null &&
  'connectorId' in value && typeof value.connectorId === 'string' &&
  'integrationUserId' in value && typeof value.integrationUserId === 'string';

const integrationIdOf = ({ connectorId, integrationUserId }: Coordinate ): string => `${ connectorId }_${ integrationUserId }`;

/**
 * TV1-2446 slice 6 (design D4, UI half) — the channel integrations (`connectorId_integrationUserId`)
 * a batch process TARGETS, for disabling Retry when one of them has a revoked credential.
 *
 * Mirrors the server's `RetryBatchProcess` refusal (`channelTargetsOfBatch` in
 * `batchProcesses.aggregate.ts`): only the kinds whose own start is refused count — the two
 * channel imports and the sync from channel. Creation, duplication and the ERP kinds are never
 * refused, so they name nothing.
 *
 * The ONE derivation every Retry surface uses: the detail page (`revokedChannelTargetsPolicy`),
 * the list's Active section and the active-run strip above the publications / inventory lists
 * (both through `revokedActiveChannelTargetsPolicy`).
 */
export const batchChannelTargets = ( process: Pick<BatchProcessesReadmodel, 'operationName' | 'data'> | null ): string[] => {
  if ( !process ) return [];
  const { operationName, data } = process;
  switch ( operationName ) {
    case 'ChannelPublicationImport':
    case 'ChannelOrderImport':
      return isCoordinate( data ) ? [integrationIdOf( data )] : [];
    case 'PublicationSyncFromChannel': {
      // Widened to `unknown[]`: `data` is the union of every kind's payload, and two kinds carry
      // a differently-shaped `publications` array; `isCoordinate` narrows each entry.
      const publications: unknown[] = 'publications' in data && Array.isArray( data.publications ) ? data.publications : [];
      return [...new Set( publications.filter( isCoordinate ).map( integrationIdOf ))];
    }
    default:
      return [];
  }
};

/** Reads one integration from the systems-integrations read model (`ExternalSystemsIntegrationsById`). */
export type ReadIntegrationCredentialState = ( integrationId: string ) => Promise<{ credentialState?: string } | null | undefined>;

/**
 * The subset of `integrationIds` whose credential is revoked, in input order.
 *
 * FAIL-OPEN like the server: an integration whose read fails counts as not revoked, so a flaky
 * read never disables Retry (the server refusal stays authoritative).
 */
export const readRevokedChannelTargets = async (
  integrationIds: readonly string[],
  readIntegration: ReadIntegrationCredentialState
): Promise<string[]> => {
  const revoked = await Promise.all( integrationIds.map( async ( integrationId ) => {
    try {
      const integration = await readIntegration( integrationId );
      return integration && isIntegrationCredentialRevoked( integration ) ? integrationId : null;
    } catch {
      return null;
    }
  }));
  return revoked.filter(( id ): id is string => id !== null );
};

/**
 * The first channel target of `process` found in `revokedIntegrationIds`, or `null`. Retry is
 * disabled while it is set, and the Reconnect call to action points at it.
 */
export const firstRevokedChannelTarget = (
  process: Pick<BatchProcessesReadmodel, 'operationName' | 'data'> | null,
  revokedIntegrationIds: readonly string[]
): string | null =>
  batchChannelTargets( process ).find(( integrationId ) => revokedIntegrationIds.includes( integrationId )) ?? null;
